import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  appsApi,
  type CreateAppInput,
  type UpdateAppInput,
} from "../api/apps.api";
import { meApi } from "../api/me.api";

const KEYS = {
  all: ["apps"] as const,
  one: (id: string) => ["apps", id] as const,
  meApps: ["me", "apps"] as const,
};

export function useApps() {
  return useQuery({ queryKey: KEYS.all, queryFn: appsApi.list });
}

export function useApp(id: string | null | undefined) {
  return useQuery({
    queryKey: KEYS.one(id ?? "_"),
    queryFn: () => appsApi.get(id!),
    enabled: !!id,
  });
}

export function useMyApps() {
  return useQuery({ queryKey: KEYS.meApps, queryFn: meApi.apps });
}

export function useReorderMyApps() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (appIds: string[]) => meApi.reorderApps(appIds),
    onSuccess: (data) => qc.setQueryData(KEYS.meApps, data),
  });
}

export function useResetMyAppsOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => meApi.resetAppOrder(),
    onSuccess: (data) => qc.setQueryData(KEYS.meApps, data),
  });
}

export function useCreateApp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateAppInput) => appsApi.create(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.all }),
  });
}

export function useUpdateApp(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateAppInput) => appsApi.update(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.all });
      qc.invalidateQueries({ queryKey: KEYS.one(id) });
    },
  });
}

export function useDeleteApp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => appsApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.all }),
  });
}
